import { Host, h } from "@stencil/core";
import { renderImg } from "../../../../common/renders";
import { ACTION_LIST_ITEM_EXPORT_PARTS, ACTION_LIST_ITEM_PARTS_DICTIONARY, ACTION_LIST_PARTS_DICTIONARY, imageTypeDictionary, KEY_CODES, startPseudoImageTypeDictionary } from "../../../../common/reserved-names";
import { tokenMap, updateDirectionInImageCustomVar } from "../../../../common/utils";
import { computeExportParts } from "./compute-exportparts";
import { computeActionTypeBlocks } from "./compute-editing-sections";
import { DEFAULT_GET_IMAGE_PATH_CALLBACK, getControlRegisterProperty } from "../../../../common/registry-properties";
const ACTION_TYPE_PARTS = {
    fix: ACTION_LIST_ITEM_PARTS_DICTIONARY.ACTION_FIX,
    modify: ACTION_LIST_ITEM_PARTS_DICTIONARY.ACTION_MODIFY,
    remove: ACTION_LIST_ITEM_PARTS_DICTIONARY.ACTION_REMOVE,
    custom: ACTION_LIST_ITEM_PARTS_DICTIONARY.ACTION_CUSTOM
};
export class ChActionListItem {
    constructor() {
        this.additionalInfo = undefined;
        this.caption = undefined;
        this.checkbox = false;
        this.checked = false;
        this.customRender = false;
        this.deleting = false;
        this.disabled = false;
        this.downloading = false;
        this.editable = undefined;
        this.editing = false;
        this.fixed = false;
        this.getImagePathCallback = undefined;
        this.indeterminate = false;
        this.metadata = undefined;
        this.nested = false;
        this.nestedExpandable = false;
        this.parts = undefined;
        this.selectable = false;
        this.selected = false;
        this.showDownloadingSpinner = true;
        this.translations = undefined;
    }
    #additionalItemListenerDictionary = {
        fix: () => this.fixedChange.emit({ itemId: this.el.id, value: !this.fixed }),
        remove: () => {
            if (!this.editing) {
                this.deleting = true;
            }
        },
        custom: callback => callback(),
        modify: () => {
            if (!this.deleting) {
                this.editing = true;
            }
        }
    };
    #confirmActionsDictionary = {
        // custom: () => this.translations.confirm,
        modify: () => this.translations.confirmModify,
        remove: () => this.translations.confirmDelete
    };
    #cancelActionsDictionary = {
        // custom: () => this.translations.cancel,
        modify: () => this.translations.cancelModify,
        remove: () => this.translations.cancelDelete
    };
    #editingSections;
    #deletingSections;
    // Refs
    #headerRef;
    #inputRef;
    el;
    additionalInfoChanged() {
        this.#setExportParts();
        this.#setActionTypeBlocks();
    }
    // /**
    //  * Fired when the checkbox value of the control is changed.
    //  */
    // @Event() checkboxChange: EventEmitter<TreeViewItemCheckedInfo>;
    // /**
    //  * Fired when the item is being dragged.
    //  */
    // @Event() itemDragStart: EventEmitter<TreeViewItemDragStartInfo>;
    /**
     * Fired when the fixed value of the control is changed.
     */
    captionChange;
    /**
     * Fired when the control is asking to modify its caption
     */
    // TODO: Unify terms (modifyCaption in the Tree View)
    fixedChange;
    /**
     * Fired when the remove button was clicked in the control.
     */
    remove;
    /**
     * Fired when the item is no longer being dragged.
     */
    itemDragEnd;
    #removeEditMode = (shouldFocusHeader, commitEdition = false) => () => {
        // When pressing the enter key in the input, the removeEditMode event is
        // triggered twice (due to the headerRef.focus() triggering the onBlur
        // event in the input), so we need to check if the edit mode was disabled
        if (!this.editing) {
            return;
        }
        this.editing = false;
        const newCaption = this.#inputRef.value;
        if (commitEdition &&
            newCaption.trim() !== "" &&
            this.caption !== newCaption) {
            this.captionChange.emit({
                itemId: this.el.id,
                newCaption: newCaption
            });
        }
        if (shouldFocusHeader) {
            this.#headerRef.focus();
        }
    };
    #checkIfShouldRemoveEditMode = (event) => {
        event.stopPropagation();
        if (event.code !== KEY_CODES.ENTER && event.code !== KEY_CODES.ESCAPE) {
            return;
        }
        event.preventDefault();
        const commitEdition = event.code === KEY_CODES.ENTER;
        this.#removeEditMode(true, commitEdition)();
    };
    #renderAdditionalItems = (additionalItems) => additionalItems.map(item => item.jsx
        ? item.jsx()
        : this.#renderAdditionalItem(item));
    #renderAdditionalItem = (item) => {
        const additionalAction = item;
        const hasImage = !!item.imgSrc;
        const hasPseudoImage = hasImage && item.imgType !== "img";
        const pseudoImageStartClass = hasPseudoImage
            ? startPseudoImageTypeDictionary[item.imgType ?? "background"]
            : null;
        const computedPseudoImage = hasPseudoImage
            ? this.#getComputedImage(additionalAction)
            : null;
        const imageTag = hasImage &&
            renderImg("img", item.part
                ? `${ACTION_LIST_ITEM_PARTS_DICTIONARY.ADDITIONAL_ITEM} ${ACTION_LIST_ITEM_PARTS_DICTIONARY.ADDITIONAL_IMAGE} ${item.part}`
                : `${ACTION_LIST_ITEM_PARTS_DICTIONARY.ADDITIONAL_ITEM} ${ACTION_LIST_ITEM_PARTS_DICTIONARY.ADDITIONAL_IMAGE}`, item.imgSrc, item.imgType);
        const action = additionalAction.action;
        // Button
        if (action) {
            const actionTypeIsFix = action.type === "fix";
            const actionTypeIsCustom = action.type === "custom";
            return (h("button", { key: additionalAction.id, "aria-label": additionalAction.accessibleName, class: {
                    "additional-item": true,
                    [pseudoImageStartClass]: hasPseudoImage && actionTypeIsCustom,
                    // TODO: Add support for these classes
                    [computedPseudoImage?.classes]: hasPseudoImage && actionTypeIsCustom && !!computedPseudoImage,
                    "show-on-mouse-hover": (actionTypeIsFix && !this.fixed) ||
                        (!actionTypeIsFix && action.showOnHover),
                    [action.type]: true,
                    fixed: actionTypeIsFix && this.fixed,
                    "not-fixed": actionTypeIsFix && !this.fixed
                }, part: tokenMap({
                    [ACTION_LIST_ITEM_PARTS_DICTIONARY.ADDITIONAL_ITEM]: true,
                    [ACTION_LIST_ITEM_PARTS_DICTIONARY.ADDITIONAL_ACTION]: true,
                    [ACTION_LIST_ITEM_PARTS_DICTIONARY.DISABLED]: this.disabled,
                    [ACTION_LIST_ITEM_PARTS_DICTIONARY.SELECTED]: this.selectable && this.selected,
                    [ACTION_LIST_ITEM_PARTS_DICTIONARY.NOT_SELECTED]: this.selectable && !this.selected,
                    [ACTION_TYPE_PARTS[action.type]]: true,
                    [ACTION_LIST_ITEM_PARTS_DICTIONARY.FIXED]: actionTypeIsFix && this.fixed,
                    [ACTION_LIST_ITEM_PARTS_DICTIONARY.NOT_FIXED]: actionTypeIsFix && !this.fixed,
                    [item.part]: !!item.part
                }), style: hasPseudoImage && actionTypeIsCustom
                    ? computedPseudoImage?.styles
                    : null, disabled: this.disabled, type: "button", onClick: !this.disabled
                    ? this.#handleAdditionalItemClick(action.type, actionTypeIsCustom ? action.callback : undefined)
                    : undefined }, actionTypeIsCustom && imageTag, item.caption && item.caption));
        }
        // Span
        if (item.caption) {
            return (h("span", { key: additionalAction.id ?? null, class: {
                    "additional-item not-actionable": true,
                    [pseudoImageStartClass]: !!pseudoImageStartClass,
                    [computedPseudoImage?.classes]: !!computedPseudoImage
                }, part: tokenMap({
                    [ACTION_LIST_ITEM_PARTS_DICTIONARY.ADDITIONAL_ITEM]: true,
                    [ACTION_LIST_ITEM_PARTS_DICTIONARY.ADDITIONAL_TEXT]: true,
                    [ACTION_LIST_ITEM_PARTS_DICTIONARY.SELECTED]: this.selectable && this.selected,
                    [ACTION_LIST_ITEM_PARTS_DICTIONARY.NOT_SELECTED]: this.selectable && !this.selected,
                    [item.part]: !!item.part
                }), style: hasPseudoImage && computedPseudoImage?.styles }, imageTag, item.caption));
        }
        // Div with background or mask
        if (hasPseudoImage) {
            return (h("div", { "aria-hidden": "true", class: {
                    "additional-item not-actionable": true,
                    [imageTypeDictionary[item.imgType ?? "background"]]: true,
                    [computedPseudoImage?.classes]: !!computedPseudoImage
                }, part: tokenMap({
                    [ACTION_LIST_ITEM_PARTS_DICTIONARY.ADDITIONAL_ITEM]: true,
                    [ACTION_LIST_ITEM_PARTS_DICTIONARY.ADDITIONAL_IMAGE]: true,
                    [ACTION_LIST_ITEM_PARTS_DICTIONARY.SELECTED]: this.selectable && this.selected,
                    [ACTION_LIST_ITEM_PARTS_DICTIONARY.NOT_SELECTED]: this.selectable && !this.selected,
                    [item.part]: !!item.part
                }), style: hasPseudoImage && computedPseudoImage?.styles }));
        }
        // Img
        if (hasImage) {
            return imageTag; // TODO: Add the "additional-item" class
        }
        return undefined;
    };
    #handleAdditionalItemClick = (type, callback) => (event) => {
        event.stopPropagation();
        if (callback) {
            callback(this.el.id);
        }
        else {
            this.#additionalItemListenerDictionary[
            // Only "custom" type has callbacks
            type]();
        }
    };
    #setExportParts = () => {
        let exportParts = undefined;
        if (this.additionalInfo) {
            const parts = computeExportParts(this.additionalInfo);
            // Additional parts
            if (parts.size > 0) {
                exportParts = `${ACTION_LIST_ITEM_EXPORT_PARTS},${Array.from(parts).join(",")}`;
            }
        }
        this.el.setAttribute("exportparts", exportParts ?? ACTION_LIST_ITEM_EXPORT_PARTS);
    };
    #setActionTypeBlocks = () => {
        this.#deletingSections = computeActionTypeBlocks("remove", this.additionalInfo);
        this.#editingSections = computeActionTypeBlocks("modify", this.additionalInfo);
    };
    #renderAdditionalInfo = (additionalModel, zoneName, stretch = false) => {
        if (!additionalModel) {
            return;
        }
        const zoneNameWithPrefix = `item__${zoneName}`;
        let actionTypeSection;
        let actionTypeAligns = undefined;
        // Editing
        if (this.editing &&
            this.#editingSections !== undefined &&
            this.#editingSections.some(editingSection => editingSection.section === zoneName)) {
            actionTypeSection = "modify";
            actionTypeAligns = this.#editingSections.find(editingSection => editingSection.section === zoneName).align;
        }
        // Deleting
        else if (this.deleting &&
            this.#deletingSections !== undefined &&
            this.#deletingSections.some(deletingSection => deletingSection.section === zoneName)) {
            actionTypeSection = "remove";
            actionTypeAligns = this.#deletingSections.find(deletingSection => deletingSection.section === zoneName).align;
        }
        return (h("div", { key: zoneNameWithPrefix, class: `align-container ${zoneName}`, part: zoneNameWithPrefix }, additionalModel.start && (h("div", { key: `${zoneNameWithPrefix}-start`, class: stretch ? "align-start valign-start" : "align-start", part: `${zoneNameWithPrefix} start` }, actionTypeSection && actionTypeAligns.includes("start")
            ? this.#renderConfirmCancelButtons(actionTypeSection)
            : this.#renderAdditionalItems(additionalModel.start))), additionalModel.center && (h("div", { key: `${zoneNameWithPrefix}-center`, class: stretch ? "align-center valign-center" : "align-center", part: `${zoneNameWithPrefix} center` }, actionTypeSection && actionTypeAligns.includes("center")
            ? this.#renderConfirmCancelButtons(actionTypeSection)
            : this.#renderAdditionalItems(additionalModel.center))), additionalModel.end && (h("div", { key: `${zoneNameWithPrefix}-end`, class: stretch ? "align-end valign-end" : "align-end", part: `${zoneNameWithPrefix} end` }, actionTypeSection && actionTypeAligns.includes("end")
            ? this.#renderConfirmCancelButtons(actionTypeSection)
            : this.#renderAdditionalItems(additionalModel.end)))));
    };
    #renderConfirmCancelButtons = (action) => [
        h("button", { "aria-label": this.#confirmActionsDictionary[action](), title: this.#confirmActionsDictionary[action](), class: "confirm-action", part: tokenMap({
                [ACTION_LIST_ITEM_PARTS_DICTIONARY.ADDITIONAL_ITEM_CONFIRM]: true,
                [ACTION_LIST_ITEM_PARTS_DICTIONARY.ACTION_ACCEPT]: true,
                [ACTION_LIST_ITEM_PARTS_DICTIONARY.DISABLED]: this.disabled,
                [ACTION_LIST_ITEM_PARTS_DICTIONARY.SELECTED]: this.selectable && this.selected,
                [ACTION_LIST_ITEM_PARTS_DICTIONARY.NOT_SELECTED]: this.selectable && !this.selected
            }), disabled: this.disabled, type: "button", onClick: !this.disabled ? this.#acceptAction(action) : undefined }),
        h("button", { "aria-label": this.#cancelActionsDictionary[action](), title: this.#cancelActionsDictionary[action](), class: "cancel-action", part: tokenMap({
                [ACTION_LIST_ITEM_PARTS_DICTIONARY.ADDITIONAL_ITEM_CONFIRM]: true,
                [ACTION_LIST_ITEM_PARTS_DICTIONARY.ACTION_CANCEL]: true,
                [ACTION_LIST_ITEM_PARTS_DICTIONARY.DISABLED]: this.disabled,
                [ACTION_LIST_ITEM_PARTS_DICTIONARY.SELECTED]: this.selectable && this.selected,
                [ACTION_LIST_ITEM_PARTS_DICTIONARY.NOT_SELECTED]: this.selectable && !this.selected
            }), disabled: this.disabled, type: "button", onClick: !this.disabled ? this.#cancelAction : undefined })
    ];
    #acceptAction = (action) => (event) => {
        event.stopPropagation();
        if (action === "modify") {
            this.#removeEditMode(true, true)();
        }
        else if (action === "remove") {
            this.remove.emit(this.el.id);
        }
    };
    #cancelAction = (event) => {
        event.stopPropagation();
        this.editing = false;
        this.deleting = false;
    };
    #getComputedImage = (additionalItem) => {
        if (!additionalItem.imgSrc) {
            return null;
        }
        // TODO: If we migrate this component to Lit, we should improve the
        // efficiency of this lookup
        const getImagePathCallback = this.getImagePathCallback ??
            getControlRegisterProperty("getImagePathCallback", "ch-action-list-render") ??
            DEFAULT_GET_IMAGE_PATH_CALLBACK;
        const img = getImagePathCallback(additionalItem);
        return img
            ? updateDirectionInImageCustomVar(img, "start")
            : null;
    };
    connectedCallback() {
        this.el.setAttribute("role", "listitem");
        this.el.setAttribute("part", ACTION_LIST_PARTS_DICTIONARY.ITEM);
        this.#setExportParts();
        this.#setActionTypeBlocks();
    }
    render() {
        const additionalInfo = this.additionalInfo;
        const hasAdditionalInfo = !!this.additionalInfo;
        const stretchStart = hasAdditionalInfo && additionalInfo["stretch-start"];
        const blockStart = hasAdditionalInfo && additionalInfo["block-start"];
        const inlineCaption = hasAdditionalInfo && additionalInfo["inline-caption"];
        const blockEnd = hasAdditionalInfo && additionalInfo["block-end"];
        const stretchEnd = hasAdditionalInfo && additionalInfo["stretch-end"];
        const hasParts = !!this.parts;
        return (h(Host, { key: '6178b35a25c32e76ee103166c985ce6ebb587ba3', "aria-selected": this.selectable && this.selected ? "true" : null }, h("button", { key: '3a73aca975b6113e11f21b938685712a6c149c56', class: "action", disabled: this.disabled, part: tokenMap({
                [ACTION_LIST_ITEM_PARTS_DICTIONARY.ACTION]: true,
                [ACTION_LIST_ITEM_PARTS_DICTIONARY.NESTED]: this.nested,
                [ACTION_LIST_ITEM_PARTS_DICTIONARY.NESTED_EXPANDABLE]: this.nestedExpandable,
                [ACTION_LIST_ITEM_PARTS_DICTIONARY.SELECTABLE]: this.selectable,
                [ACTION_LIST_ITEM_PARTS_DICTIONARY.NOT_SELECTABLE]: !this.selectable,
                [ACTION_LIST_ITEM_PARTS_DICTIONARY.SELECTED]: this.selectable && this.selected,
                [ACTION_LIST_ITEM_PARTS_DICTIONARY.NOT_SELECTED]: this.selectable && !this.selected,
                [ACTION_LIST_ITEM_PARTS_DICTIONARY.DISABLED]: this.disabled
            }), type: "button", ref: el => (this.#headerRef = el) }, this.#renderAdditionalInfo(stretchStart, "stretch-start", true), this.#renderAdditionalInfo(blockStart, "block-start"), h("div", { key: "item__inline-caption", class: "align-container inline-caption", part: tokenMap({
                "item__inline-caption": true,
                [ACTION_LIST_ITEM_PARTS_DICTIONARY.EDITING]: this.editing,
                [ACTION_LIST_ITEM_PARTS_DICTIONARY.NOT_EDITING]: !this.editing
            }) }, this.editing ? (h("ch-edit", { autoFocus: true, part: hasParts
                ? `${ACTION_LIST_ITEM_PARTS_DICTIONARY.EDIT_CAPTION} ${this.parts}`
                : ACTION_LIST_ITEM_PARTS_DICTIONARY.EDIT_CAPTION, disabled: this.disabled, value: this.caption, onKeyDown: !this.disabled ? this.#checkIfShouldRemoveEditMode : undefined, ref: el => (this.#inputRef = el) })) : (this.caption && (h("span", { part: ACTION_LIST_ITEM_PARTS_DICTIONARY.CAPTION }, this.caption))), inlineCaption && [
            inlineCaption.start && (h("div", { key: "item__inline-caption-start", class: "align-start", part: "item__inline-caption start" }, this.#renderAdditionalItems(inlineCaption.start))),
            inlineCaption.end && (h("div", { key: "item__inline-caption-end", class: "align-end", part: "item__inline-caption end" }, this.#renderAdditionalItems(inlineCaption.end)))
        ]), this.#renderAdditionalInfo(blockEnd, "block-end"), this.#renderAdditionalInfo(stretchEnd, "stretch-end"))));
    }
    static get is() { return "ch-action-list-item"; }
    static get encapsulation() { return "shadow"; }
    static get delegatesFocus() { return true; }
    static get originalStyleUrls() {
        return {
            "$": ["action-list-item.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["action-list-item.css"]
        };
    }
    static get properties() {
        return {
            "additionalInfo": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "ActionListItemAdditionalInformation",
                    "resolved": "{ \"stretch-start\"?: ActionListItemAdditionalModel; \"block-start\"?: ActionListItemAdditionalModel; \"inline-caption\"?: ActionListItemAdditionalModel; \"block-end\"?: ActionListItemAdditionalModel; \"stretch-end\"?: ActionListItemAdditionalModel; }",
                    "references": {
                        "ActionListItemAdditionalInformation": {
                            "location": "import",
                            "path": "../../types",
                            "id": "src/components/action-list/types.ts::ActionListItemAdditionalInformation"
                        }
                    }
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": ""
                }
            },
            "caption": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attributes specifies the caption of the control."
                },
                "attribute": "caption",
                "reflect": false
            },
            "checkbox": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Set this attribute if you want display a checkbox in the control."
                },
                "attribute": "checkbox",
                "reflect": false,
                "defaultValue": "false"
            },
            "checked": {
                "type": "boolean",
                "mutable": true,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Set this attribute if you want the checkbox to be checked by default.\nOnly works if `checkbox = true`"
                },
                "attribute": "checked",
                "reflect": true,
                "defaultValue": "false"
            },
            "customRender": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Set this attribute if you want to set a custom render for the control, by\npassing a slot."
                },
                "attribute": "custom-render",
                "reflect": false,
                "defaultValue": "false"
            },
            "deleting": {
                "type": "boolean",
                "mutable": true,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Set this property when the control is in delete mode."
                },
                "attribute": "deleting",
                "reflect": false,
                "defaultValue": "false"
            },
            "disabled": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute lets you specify if the element is disabled.\nIf disabled, it will not fire any user interaction related event\n(for example, click event)."
                },
                "attribute": "disabled",
                "reflect": true,
                "defaultValue": "false"
            },
            "downloading": {
                "type": "boolean",
                "mutable": true,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute lets you specify when items are being lazy loaded in the\ncontrol."
                },
                "attribute": "downloading",
                "reflect": false,
                "defaultValue": "false"
            },
            "editable": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This property lets you specify if the edit operation is enabled in the\ncontrol. If `true`, the control can edit its caption in place."
                },
                "attribute": "editable",
                "reflect": false
            },
            "editing": {
                "type": "boolean",
                "mutable": true,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Set this property when the control is in edit mode."
                },
                "attribute": "editing",
                "reflect": false,
                "defaultValue": "false"
            },
            "fixed": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "fixed",
                "reflect": false,
                "defaultValue": "false"
            },
            "getImagePathCallback": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "(\n    item: ActionListItemAdditionalBase\n  ) => GxImageMultiState | undefined",
                    "resolved": "(item: ActionListItemAdditionalBase) => GxImageMultiState",
                    "references": {
                        "ActionListItemAdditionalBase": {
                            "location": "import",
                            "path": "../../types",
                            "id": "src/components/action-list/types.ts::ActionListItemAdditionalBase"
                        },
                        "GxImageMultiState": {
                            "location": "import",
                            "path": "../../../../common/types",
                            "id": "src/common/types.ts::GxImageMultiState"
                        }
                    }
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "This property specifies a callback that is executed when the path for an\nimgSrc needs to be resolved."
                }
            },
            "indeterminate": {
                "type": "boolean",
                "mutable": true,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "`true` if the checkbox's value is indeterminate."
                },
                "attribute": "indeterminate",
                "reflect": false,
                "defaultValue": "false"
            },
            "metadata": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute represents additional info for the control that is included\nwhen dragging the item."
                },
                "attribute": "metadata",
                "reflect": false
            },
            "nested": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies if the item is inside of a ch-action-list-group control."
                },
                "attribute": "nested",
                "reflect": false,
                "defaultValue": "false"
            },
            "nestedExpandable": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies if the item is inside of a ch-action-list-group control that\nis expandable."
                },
                "attribute": "nested-expandable",
                "reflect": false,
                "defaultValue": "false"
            },
            "parts": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Specifies a set of parts to use in every DOM element of the control."
                },
                "attribute": "parts",
                "reflect": false
            },
            "selectable": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies if the item can be selected."
                },
                "attribute": "selectable",
                "reflect": false,
                "defaultValue": "false"
            },
            "selected": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute lets you specify if the item is selected"
                },
                "attribute": "selected",
                "reflect": false,
                "defaultValue": "false"
            },
            "showDownloadingSpinner": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "`true` to show the downloading spinner when lazy loading the sub items of\nthe control."
                },
                "attribute": "show-downloading-spinner",
                "reflect": false,
                "defaultValue": "true"
            },
            "translations": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "ActionListTranslations",
                    "resolved": "{ confirmDelete: string; cancelDelete: string; confirmModify: string; cancelModify: string; }",
                    "references": {
                        "ActionListTranslations": {
                            "location": "import",
                            "path": "../../translations",
                            "id": "src/components/action-list/translations.ts::ActionListTranslations"
                        }
                    }
                },
                "required": true,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the literals required for the control."
                }
            }
        };
    }
    static get events() {
        return [{
                "method": "captionChange",
                "name": "captionChange",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when the fixed value of the control is changed."
                },
                "complexType": {
                    "original": "ActionListCaptionChangeEventDetail",
                    "resolved": "{ itemId: string; newCaption: string; }",
                    "references": {
                        "ActionListCaptionChangeEventDetail": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/action-list/internal/action-list-item/types.ts::ActionListCaptionChangeEventDetail"
                        }
                    }
                }
            }, {
                "method": "fixedChange",
                "name": "fixedChange",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when the control is asking to modify its caption"
                },
                "complexType": {
                    "original": "ActionListFixedChangeEventDetail",
                    "resolved": "{ itemId: string; value: boolean; }",
                    "references": {
                        "ActionListFixedChangeEventDetail": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/action-list/internal/action-list-item/types.ts::ActionListFixedChangeEventDetail"
                        }
                    }
                }
            }, {
                "method": "remove",
                "name": "remove",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when the remove button was clicked in the control."
                },
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                }
            }, {
                "method": "itemDragEnd",
                "name": "itemDragEnd",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when the item is no longer being dragged."
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }];
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "additionalInfo",
                "methodName": "additionalInfoChanged"
            }];
    }
}
